import { useParams, Link } from "react-router-dom";
import { useMemo } from "react";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

const categoryLabelMap: Record<string, string> = {
  "hot-tracking": "热点追踪",
  "deep-dive": "深度揭秘",
  "entertainment": "娱乐八卦",
  "society": "社会万象",
  "tech": "科技数码",
  "finance": "财经内幕",
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
      .slice(0, 4);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 px-4 md:px-6 py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:underline mt-4 inline-block">返回首页</Link>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabel = categoryLabelMap[article.categorySlug] || article.categorySlug;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    publisher: {
      "@type": "Organization",
      name: "黑料社区",
      logo: {
        "@type": "ImageObject",
        url: "https://heiliaoshequ.cn/logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://heiliaoshequ.cn/article/${article.id}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${article.title} - 黑料社区`}
        description={article.excerpt}
        keywords={article.tags.join(",") + ",黑料社区,爆料资讯"}
        canonicalUrl={`https://heiliaoshequ.cn/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        articlePublishedTime={article.publishDate}
        articleModifiedTime={article.publishDate}
        articleAuthor={article.author}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main className="pt-20">
        <article className="w-full px-4 md:px-6 pb-10 md:pb-14">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: categoryLabel, path: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}

            />

            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 text-xs bg-primary-100/80 text-primary-700 rounded-full font-medium cursor-pointer hover:bg-primary-200/80 transition-colors"
                >
                  {categoryLabel}
                </Link>
                {article.isHot && (
                  <span className="px-3 py-1 text-xs bg-accent-100 text-accent-700 rounded-full font-medium">
                    热门
                  </span>
                )}
              </div>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i> {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()} 阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i> {article.readTime} 分钟阅读
                </span>
              </div>
            </header>

            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 黑料社区`}
                className="w-full h-auto object-cover"
              />
            </div>

            <div
              className="prose prose-base md:prose-lg max-w-none text-foreground-800
                [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-6 [&_h3]:mb-3
                [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-foreground-700
                [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4
                [&_li]:mb-1.5 [&_li]:text-foreground-700 [&_li]:leading-relaxed
                [&_blockquote]:border-l-4 [&_blockquote]:border-primary-300 [&_blockquote]:bg-background-100/70 [&_blockquote]:p-4 [&_blockquote]:rounded-r-lg [&_blockquote]:mb-4
                [&_strong]:text-foreground-950 [&_strong]:font-semibold
              "
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between p-4 bg-background-100/70 rounded-xl">
              <div>
                <p className="text-sm text-foreground-600">
                  觉得文章有用？分享给更多人看到
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button className="w-9 h-9 flex items-center justify-center bg-secondary-100 hover:bg-secondary-200 rounded-lg cursor-pointer transition-colors" aria-label="分享到微信">
                  <i className="ri-wechat-line text-secondary-700"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center bg-secondary-100 hover:bg-secondary-200 rounded-lg cursor-pointer transition-colors" aria-label="分享到微博">
                  <i className="ri-weibo-line text-secondary-700"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center bg-secondary-100 hover:bg-secondary-200 rounded-lg cursor-pointer transition-colors" aria-label="复制链接">
                  <i className="ri-link text-secondary-700"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100/70 py-10 md:py-14">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">相关文章</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}